import { selectors } from "../add-element/cypressSelectors";
import { AddressFields } from "../resources/modelTypes";
import { format as formatDate } from "date-fns";
import React from "react";

export const formatAddress = ({
  address1,
  address2,
  city,
  state,
  zipCode,
}: Partial<AddressFields>) => {
  // Return empty string if all required fields are null/undefined/empty
  if (!address1 && !city && !state && !zipCode) {
    return "";
  }

  // Filter and join the comma-separated parts: address1, address2?, city, state
  const addressParts = [address1, address2, city, state].filter(Boolean);

  let result = addressParts.join(", ");

  // Handle zipCode separately (space-separated from state)
  if (zipCode) {
    result += ` ${zipCode}`;
  }

  return result;
};

export const formatEventDate = (date: string) => {
  // incoming date format: 'MM/dd/yyyy'
  const [month, day, year] = date.split("/");

  if (!(day && month && year)) {
    return "";
  }

  const actualDate = new Date(Number(year), Number(month) - 1, Number(day));

  return formatDate(actualDate, "EEEE, MMMM d, yyyy");
};

/** Same input as {@link formatEventDate}; output matches Minted host email (e.g. August 07, 2026). */
const formatEventDateEmail = (date: string) => {
  const [month, day, year] = date.split("/");

  if (!(day && month && year)) {
    return "";
  }

  const actualDate = new Date(Number(year), Number(month) - 1, Number(day));

  return formatDate(actualDate, "MMMM dd, yyyy");
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST", "CDT") from IANA timezone string.
 */
export const getTimezoneAbbreviation = (
  timezone: string | null | undefined,
): string => {
  if (!timezone) {
    return "";
  }

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());

    return parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

export const formatEventTime = (
  date: string,
  time: string,
  timezone?: string | null,
) => {
  const timezoneAbbr = getTimezoneAbbreviation(timezone);
  const timeDisplay = timezoneAbbr ? `${time} ${timezoneAbbr}` : time;

  return (
    <div>
      {date}
      <br />
      {timeDisplay}
    </div>
  );
};

/**
 * Single-line date/time for guest email preview (Minted transactional email style),
 * e.g. `7:00 PM - 10:30 PM, August 07, 2026`.
 */
export const getEventDateTimeComponentEmailPreview = (
  date: string | undefined,
  time: string | undefined,
  endDate: string | undefined,
  endTime: string | undefined,
) => {
  if (!date) {
    return null;
  }

  const dateEmail = formatEventDateEmail(date);
  const dataCy = `${selectors.sections_collapsedField}_datetime`;

  const wrap = (text: string) => <span data-cy={dataCy}>{text}</span>;

  if (!endDate) {
    if (time) {
      return wrap(`${time}, ${dateEmail}`);
    }
    return wrap(dateEmail);
  }

  if (date === endDate && time !== endTime) {
    if (endTime) {
      return wrap(`${time} - ${endTime}, ${dateEmail}`);
    }
    const timeOnly = time || "";
    return wrap(`${timeOnly}, ${dateEmail}`);
  }

  if (date !== endDate) {
    const endDateEmail = formatEventDateEmail(endDate);
    const startPart = time ? `${time}, ${dateEmail}` : dateEmail;
    const endPart = endTime ? `${endTime}, ${endDateEmail}` : endDateEmail;
    return wrap(`${startPart} - ${endPart}`);
  }

  if (time) {
    return wrap(`${time}, ${dateEmail}`);
  }
  return wrap(dateEmail);
};

export const getEventDateTimeComponent = (
  date: string | undefined,
  time: string | undefined,
  endDate: string | undefined,
  endTime: string | undefined,
  timezone?: string | null,
) => {
  // Incoming date format: 'MM/dd/yyyy'

  // Date is required to be valued
  if (!date) {
    return null;
  }

  const formattedStartDate = formatEventDate(date);
  const timezoneAbbr = getTimezoneAbbreviation(timezone);

  const startDateTime = time
    ? formatEventTime(formattedStartDate, time, timezone)
    : formattedStartDate;

  if (!endDate) {
    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {startDateTime}
      </div>
    );
  }

  if (date === endDate && time !== endTime) {
    const timeWithTz = timezoneAbbr
      ? `${time} - ${endTime} ${timezoneAbbr}`
      : `${time} - ${endTime}`;
    const timeRange = endTime ? (
      <span>
        <br />
        {timeWithTz}
      </span>
    ) : timezoneAbbr ? (
      `${time} ${timezoneAbbr}`
    ) : (
      time
    );

    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {`${formattedStartDate}`}
        {timeRange}
      </div>
    );
  }

  if (date !== endDate) {
    const formattedEndDate = formatEventDate(endDate);
    const endDateTime = endTime
      ? formatEventTime(formattedEndDate, endTime, timezone)
      : formattedEndDate;

    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {startDateTime}
        -
        <br />
        {endDateTime}
      </div>
    );
  }

  return (
    <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
      {startDateTime}
    </div>
  );
};
